/**
 * §6 — cdnjs.cloudflare.com runtime loader.
 *
 * Heavy animation libs (GSAP, ScrollTrigger, Lenis) are NOT bundled: they load from
 * Cloudflare's library CDN, where they're very likely already in the visitor's HTTP cache.
 * Rules implemented here (normative, see HELIX-MASTER-BUILD-GUIDE.md §6):
 *   1. one in-flight promise per URL (StrictMode double-mount safe)
 *   2. async script, crossorigin=anonymous, resolves on load / rejects on error or 8s timeout
 *   3. preconnect lives in index.html
 *   4. callers MUST handle rejection — the page degrades, never breaks
 *   5. add SRI `integrity` attrs at release time (see guide for generation command)
 */

const CDN = 'https://cdnjs.cloudflare.com/ajax/libs';

export const CDN_URLS = {
  gsap: `${CDN}/gsap/3.12.5/gsap.min.js`,
  scrollTrigger: `${CDN}/gsap/3.12.5/ScrollTrigger.min.js`,
  // DEVIATION: guide §6 pins lenis on cdnjs, but cdnjs has never hosted Lenis
  // (their API returns "Library not found"). Same pinned version from jsDelivr.
  lenis: 'https://cdn.jsdelivr.net/npm/lenis@1.1.13/dist/lenis.min.js',
  // Monaco (§9.4, Phase 2) — AMD bootstrap lives in src/lib/monaco.ts
  monacoBase: `${CDN}/monaco-editor/0.52.2/min`,
} as const;

/** Pins for the SANDBOXED PREVIEW iframe (React projects, see Builder.tsx):
 *  Babel transpiles TSX in-browser, esm.sh serves the React module graph
 *  (its ?deps pinning keeps every package on ONE react instance — jsdelivr
 *  +esm can't dedupe peers, which crashes hooks), Tailwind Play compiles
 *  utility classes at runtime. All free, keyless CDNs. */

/** react-router-dom is resolved to a SHIM in the editor preview. Its srcdoc
 *  iframe is a null/opaque origin where `history.pushState`/`replaceState` throw
 *  SecurityError and `location.pathname` reads as "srcdoc".
 *  So `BrowserRouter`/`HashRouter` can't navigate and match no route (blank page);
 *  `MemoryRouter` is the ONLY router that works there. The shim re-exports the real
 *  package but aliases Browser/HashRouter → MemoryRouter (and create*Router →
 *  createMemoryRouter), so a generated app routes correctly no matter which router
 *  the model emits — robust to aliased/namespace imports because it rewrites the
 *  MODULE, not the user's source. Same esm.sh URL on both sides ⇒ ONE react-router
 *  instance ⇒ shared Router context. (Reproduced + verified in a real null-origin
 *  frame; see the CODER prompt, which should also emit MemoryRouter.) Published
 *  projects use the real module through PUBLISHED_IMPORT_MAP below. */
const RRD_PREVIEW = 'https://esm.sh/react-router-dom@6.26.2?deps=react@18.3.1,react-dom@18.3.1';
const RRD_PREVIEW_SHIM = 'data:text/javascript,' + encodeURIComponent(
  `export * from ${JSON.stringify(RRD_PREVIEW)};\n`
  + `import { MemoryRouter as __M, createMemoryRouter as __cm } from ${JSON.stringify(RRD_PREVIEW)};\n`
  + `export { __M as BrowserRouter, __M as HashRouter, __cm as createBrowserRouter, __cm as createHashRouter };\n`,
);

export const PREVIEW_CDN = {
  babel: `${CDN}/babel-standalone/7.24.7/babel.min.js`,
  tailwind: 'https://cdn.tailwindcss.com/3.4.16',
  importMap: {
    react: 'https://esm.sh/react@18.3.1',
    'react/jsx-runtime': 'https://esm.sh/react@18.3.1/jsx-runtime',
    'react-dom': 'https://esm.sh/react-dom@18.3.1?deps=react@18.3.1',
    'react-dom/client': 'https://esm.sh/react-dom@18.3.1/client?deps=react@18.3.1',
    'react-router-dom': RRD_PREVIEW_SHIM,
  },
} as const;

/** Published projects have an isolated same-origin host, so URL history works. */
export const PUBLISHED_IMPORT_MAP = {
  ...PREVIEW_CDN.importMap,
  'react-router-dom': RRD_PREVIEW,
} as const;

const inflight = new Map<string, Promise<void>>();

export function loadScript(url: string, timeoutMs = 8000): Promise<void> {
  const existing = inflight.get(url);
  if (existing) return existing;

  const p = new Promise<void>((resolve, reject) => {
    if (typeof document === 'undefined') return reject(new Error('no DOM'));
    const el = document.createElement('script');
    el.src = url;
    el.async = true;
    el.crossOrigin = 'anonymous';
    const timer = window.setTimeout(() => {
      reject(new Error(`CDN timeout: ${url}`));
    }, timeoutMs);
    el.onload = () => {
      window.clearTimeout(timer);
      resolve();
    };
    el.onerror = () => {
      window.clearTimeout(timer);
      inflight.delete(url); // allow retry on next mount
      reject(new Error(`CDN failed: ${url}`));
    };
    document.head.appendChild(el);
  });

  inflight.set(url, p);
  return p;
}

/* ── Minimal ambient types for the UMD globals we use ───────────────────── */

export interface ScrollTriggerSelf {
  progress: number;
  direction: number;
  end: number;
  kill: () => void;
}

export interface GsapGlobal {
  registerPlugin: (...plugins: unknown[]) => void;
  ticker: {
    add: (fn: (time: number) => void) => void;
    remove: (fn: (time: number) => void) => void;
    lagSmoothing: (threshold: number, adjustedLag?: number) => void;
  };
  to: (target: object, vars: Record<string, unknown>) => { kill: () => void };
}

export interface ScrollTriggerGlobal {
  update: () => void;
  refresh: () => void;
  create: (vars: Record<string, unknown>) => ScrollTriggerSelf;
  getAll: () => ScrollTriggerSelf[];
  /** global options — used for ignoreMobileResize (URL-bar show/hide jolts) */
  config: (vars: Record<string, unknown>) => void;
}

export interface LenisInstance {
  raf: (time: number) => void;
  on: (event: 'scroll', cb: () => void) => void;
  destroy: () => void;
  scrollTo: (target: number, opts?: { immediate?: boolean }) => void;
  stop: () => void;
  start: () => void;
}

export interface LenisCtor {
  new (opts?: {
    lerp?: number;
    smoothWheel?: boolean;
    wheelMultiplier?: number;
    touchMultiplier?: number;
    // both default to 'vertical' in lenis@1.1.13 — set explicitly to lock the
    // wheel/trackpad horizontal case (touch is handled by CSS touch-action)
    orientation?: 'vertical' | 'horizontal';
    gestureOrientation?: 'vertical' | 'horizontal' | 'both';
  }): LenisInstance;
}

declare global {
  interface Window {
    gsap?: GsapGlobal;
    ScrollTrigger?: ScrollTriggerGlobal;
    Lenis?: LenisCtor;
  }
}

/** Load GSAP core + ScrollTrigger and register the plugin. Throws on failure. */
export async function loadGsap(): Promise<{ gsap: GsapGlobal; ScrollTrigger: ScrollTriggerGlobal }> {
  await loadScript(CDN_URLS.gsap);
  await loadScript(CDN_URLS.scrollTrigger);
  const gsap = window.gsap;
  const ScrollTrigger = window.ScrollTrigger;
  if (!gsap || !ScrollTrigger) throw new Error('GSAP globals missing after load');
  gsap.registerPlugin(ScrollTrigger);
  return { gsap, ScrollTrigger };
}

/** Load Lenis (smooth scroll). Returns the constructor, or null if the CDN failed — callers degrade to native scroll. */
export async function loadLenis(): Promise<LenisCtor | null> {
  try {
    await loadScript(CDN_URLS.lenis);
    return window.Lenis ?? null;
  } catch {
    return null;
  }
}
